import type { ReactNode } from 'react';
import { Inbox, LayoutList, Bot, History, Plug, BookOpen } from 'lucide-react';
import type { OverviewStats } from '@/types';
import { formatCompactNumber } from '@/lib/format-number';

interface StatsRowProps {
  stats?: OverviewStats;
  activeRunsCount?: number;
  isLoading?: boolean;
}

interface StatCardCellProps {
  icon: ReactNode;
  title: string;
  value: number | string;
}

function StatCardCell({ icon, title, value }: StatCardCellProps) {
  return (
    <div className="flex flex-col items-center rounded-lg border border-border bg-card p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-600 dark:bg-primary-900/30">
          {icon}
        </span>
        <p className="text-sm font-medium text-muted-foreground leading-snug text-left whitespace-pre-line">
          {title}
        </p>
      </div>
      <p className="mt-3 text-3xl font-semibold tabular-nums">{value}</p>
    </div>
  );
}

function display(value: number | undefined, isLoading: boolean | undefined): string {
  if (isLoading && value === undefined) return '...';
  return formatCompactNumber(value ?? 0);
}

export function StatsRow({ stats, activeRunsCount, isLoading }: StatsRowProps) {
  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
      <StatCardCell
        title={"Inbox\nUnread"}
        value={display(stats?.inboxUnread, isLoading)}
        icon={<Inbox className="h-5 w-5" />}
      />
      <StatCardCell
        title={"Active\nAgents"}
        value={display(activeRunsCount, isLoading)}
        icon={<Bot className="h-5 w-5" />}
      />
      <StatCardCell
        title={"Today's\nAgent Runs"}
        value={display(stats?.todayAgentRuns, isLoading)}
        icon={<History className="h-5 w-5" />}
      />
      <StatCardCell
        title={"Today's\nActivities"}
        value={display(stats?.todayActivities, isLoading)}
        icon={<LayoutList className="h-5 w-5" />}
      />
      <StatCardCell
        title={"Integration\nEvents"}
        value={display(stats?.todayIntegrationEvents, isLoading)}
        icon={<Plug className="h-5 w-5" />}
      />
      <StatCardCell
        title="Documents"
        value={display(stats?.totalKbDocuments, isLoading)}
        icon={<BookOpen className="h-5 w-5" />}
      />
    </div>
  );
}
